import { notNull } from "@msys/common";
import { Alert, AlertTitle, Button } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { Link } from "react-router-dom";
import { useUserData } from "../../../auth/useUserData.js";
import { WarningsIconButton } from "../../../commons/button/WarningsIconButton.js";
import { TemplateQuoteWarnings_QuoteTemplateFragment } from "./TemplateQuoteWarnings.generated.js";
import { QuoteTemplateUpdateShareButton } from "./buttons/QuoteTemplateUpdateShareButton.js";
import { isNil, isNull } from "lodash-es";

export const useTemplateQuoteWarnings = () => {
  const { t } = useTranslate(["Global", "Templates"]);
  const viewer = useUserData().currentUser!;

  const getTemplateQuoteWarnings = (
    pathToDoc: string,
    doc: TemplateQuoteWarnings_QuoteTemplateFragment
  ) => {
    const subscribedToTemplate = doc.templateSharing?.subscribedToTemplate;
    const isLatestPublishedVersion =
      !!doc.latestPublishedVersionNumber &&
      !!doc.resolvedAsReadModelVersionNumber &&
      doc.resolvedAsReadModelVersionNumber === doc.latestPublishedVersionNumber;
    const isOwnTemplate =
      doc.owningSystemOrganisationId === viewer.organisation.id;
    const latestSharedVersionNumber =
      doc.templateSharing?.latestSharedVersionNumber;
    const isSharingNewVersionAvailable =
      !isNil(doc.latestPublishedVersionNumber) &&
      !isNil(latestSharedVersionNumber) &&
      latestSharedVersionNumber < doc.latestPublishedVersionNumber;
    const isSharedNewVersionAvailable =
      !isNil(doc.resolvedAsReadModelVersionNumber) &&
      !isNil(latestSharedVersionNumber) &&
      doc.resolvedAsReadModelVersionNumber < latestSharedVersionNumber;

    const warnings: React.ReactNode[] = [
      isSharedNewVersionAvailable ? (
        <Alert
          key={"outdated-version"}
          variant="outlined"
          severity="warning"
          action={
            <Button
              component={Link}
              to={`${pathToDoc}/${latestSharedVersionNumber}`}
              color="inherit"
            >
              {t("View", { ns: "Global" })}
            </Button>
          }
        >
          <AlertTitle>
            {t("Outdated template!", { ns: "Templates" })}
          </AlertTitle>
          {t("There is a newer version of this template available.", {
            ns: "Templates",
          })}
        </Alert>
      ) : null,
      isOwnTemplate &&
      isLatestPublishedVersion &&
      doc.containsUnpublishedChanged &&
      viewer.organisationPermissions.includes("template_document_update") ? (
        <Alert
          key={"unpublished-changes"}
          variant="outlined"
          severity="warning"
          action={
            <Button
              component={Link}
              to={`${pathToDoc}/latest/edit`}
              color="inherit"
            >
              {t("View", {
                ns: "Global",
              })}
            </Button>
          }
        >
          <AlertTitle>
            {t("Unpublished changes pending!", {
              ns: "Templates",
            })}
          </AlertTitle>
          {t(
            "This template contains changes that have not yet been published.",
            {
              ns: "Templates",
            }
          )}
        </Alert>
      ) : null,
      isOwnTemplate &&
      isSharingNewVersionAvailable &&
      doc.latestPublishedVersionNumber ? (
        <Alert
          key={"sharing-update"}
          variant="outlined"
          severity="warning"
          action={
            <QuoteTemplateUpdateShareButton
              doc={doc}
              buttonProps={{
                color: "inherit",
                variant: "text",
                startIcon: null,
              }}
              buttonTitle={t("Update", { ns: "Global" })}
              versionNumber={doc.latestPublishedVersionNumber}
            />
          }
        >
          <AlertTitle>
            {t("Unpublished changes on Meister Store!", {
              ns: "Templates",
            })}
          </AlertTitle>
          {t(
            "This template shared version on Meister Store is behind latest published version.",
            {
              ns: "Templates",
            }
          )}
        </Alert>
      ) : null,
    ].filter(notNull);

    return warnings;
  };

  const getTemplateQuoteWarningIcon = (
    pathToDoc: string,
    doc: TemplateQuoteWarnings_QuoteTemplateFragment
  ) => {
    const warnings = getTemplateQuoteWarnings(pathToDoc, doc);
    return warnings.length > 0 ? (
      <WarningsIconButton key={"warnings"} warnings={warnings} />
    ) : null;
  };

  return { getTemplateQuoteWarnings, getTemplateQuoteWarningIcon };
};
